'use client';

import React, { useState, useEffect } from 'react';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import Text from '@/components/Text/Text';
import { mediaPath } from '@/mediaPath';
import styles from './Hero.module.scss';
import Button, { ButtonType } from '@/components/Button/Button';
import Input, { InputVariant } from '@/components/Input/Input';
import { isValidEmail, getEmailErrorMessage } from '@/helpers';
import apiClient from '@/apiClient';

type HeroProps = {
  data: {
    background: {
      name?: string;
      url?: string;
    };
    logo: {
      name: string;
      width: number;
      height: number;
      url: string;
    };
    title?: string;
    titleHtml?: string;
    buttons?: {
      id: number;
      title: string;
      type: ButtonType;
      link: string;
      iconName: string;
      inputPlaceholder?: string;
    }[]
  };
  stripeLink: string;
};

const Hero: React.FC<HeroProps> = ({ data, stripeLink }) => {
  const inputRow = data.buttons?.find((btn) => btn.inputPlaceholder);
  const actionButtons = data.buttons?.filter((btn) => !btn.inputPlaceholder);
  const [email, setEmail] = useState('');
  const [feedback, setFeedback] = useState<{
    variant: InputVariant;
    message: string;
  }>({
    variant: InputVariant.DEFAULT,
    message: '',
  });

  const t = useTranslations('invite');
  const tHome = useTranslations('Home');

  useEffect(() => {
    if (feedback.variant !== InputVariant.DEFAULT) {
      setEmail('');
      const timer = setTimeout(() => {
        setFeedback({ variant: InputVariant.DEFAULT, message: '' });
      }, 3_000);
      return () => clearTimeout(timer);
    }
  }, [feedback]);

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
    if (feedback.variant !== InputVariant.DEFAULT) {
      setFeedback({ variant: InputVariant.DEFAULT, message: '' });
    }
  };

  const sendEmail = async (emailParam: string) => apiClient.post<{ success: boolean; message?: string }>('email', {
    cache: 'no-cache',
    body: JSON.stringify({
      email: emailParam,
      type: 'Website Invitation form submitted',
    }),
  });

  const handleSendClick = async () => {
    if (!isValidEmail(email)) {
      setFeedback({
        variant: InputVariant.ERROR,
        message: getEmailErrorMessage(email),
      });
      return;
    }

    // open in new tab the PREORDER_URL
    window.open(stripeLink, '_blank');

    try {
      await sendEmail(email);
      setFeedback({
        variant: InputVariant.SUCCESS,
        message: t('Email successfully sent!'),
      });
    } catch (error) {
      console.warn(error);
      setFeedback({
        variant: InputVariant.ERROR,
        message: t('Something went wrong, Please try again'),
      });
    }
  };

  return (
    <section
      className={clsx(styles.hero, 'custom')}
      style={data.background.url ? {
        backgroundImage: `linear-gradient(180deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0) 70.52%), url(${mediaPath(data.background.url)})`,
        backgroundColor: 'lightgray',
        backgroundRepeat: 'no-repeat',
      } : undefined}
    >
      <div className={styles.content}>
        <div className={styles.topRow}>
          {data.titleHtml && (
            <div
              className={styles.titleHtml}
              dangerouslySetInnerHTML={{
                __html: data.titleHtml,
              }}
            />
          )}
          {inputRow && (
            <div className={styles.inputRow}>
              <Input
                type='email'
                placeholder={inputRow.inputPlaceholder}
                onChange={handleEmailChange}
                value={email}
                variant={feedback.variant}
                text={feedback.message}
                className={styles.emailInput}
                showIcon
              />
              <div>
                <Button
                  onClick={handleSendClick}
                  type={inputRow.type}
                  iconName={inputRow.iconName}
                  className={styles.inputButton}
                >
                  {inputRow.title}
                </Button>
              </div>
            </div>
          )}
          {data.title && (
            <Text
              tag='p'
              className={styles.title}
            >
              {data.title}
            </Text>
          )}
          {!!actionButtons?.length && (
            <div className={styles.buttons}>
                {actionButtons.map((btn) => (
                  <div
                    className={styles.buttonWrapper}
                    key={btn.id}
                  >
                    <Button
                      type={btn.type}
                      fitContent
                      href={btn.link}
                      iconName={btn.iconName}
                    >
                      {btn.title}
                    </Button>
                    {btn.link === 'https://personalisation.artofyou.life/' && <span className={styles.hintText}>{tHome('*takes only 5 minutes')}</span>}
                  </div>
                ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default Hero;
